import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import IconSVG from './IconSVG';
import logger from '../../utils/logger';

export interface DropdownOption {
  value: string;
  label: string;
  icon?: string;
}

export interface DropdownProps {
  options: DropdownOption[];
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  width?: string;
  menuWidth?: string;
  testId?: string;
  fullWidth?: boolean; // Added for compatibility with other form components
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  placeholder = 'Select an option',
  onChange,
  className = '',
  disabled = false,
  label,
  error,
  width = 'w-full',
  menuWidth,
  testId,
  fullWidth, // Ignored since width is used instead
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Simple type-aware value matching
  logger.info('[Dropdown] Finding option for value:', value);
  
  let selectedOption = null;
  
  if (value !== undefined && value !== null) {
    // Try exact match first
    selectedOption = options.find(option => option.value === value);
    
    // If no match and types differ, try string comparison
    if (!selectedOption) {
      const valueStr = String(value);
      selectedOption = options.find(option => String(option.value) === valueStr);
    }
    
    logger.info('[Dropdown] Selected option:', selectedOption || 'none');
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionClick = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div 
      className={classNames('relative', width, className)} 
      ref={dropdownRef}
      data-testid={testId}
    >
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <div
        className={classNames(
          'flex items-center justify-between px-3 py-2 border rounded-md shadow-sm cursor-pointer bg-white dark:bg-gray-800',
          {
            'border-gray-300 dark:border-gray-600': !error && !isOpen,
            'border-blue-500 ring-1 ring-blue-500': isOpen && !error,
            'border-red-500 ring-1 ring-red-500': error,
            'opacity-50 cursor-not-allowed': disabled,
          }
        )}
        onClick={toggleDropdown}
      >
        <div className="flex items-center">
          {selectedOption?.icon && (
            <IconSVG icon={selectedOption.icon} className="mr-2 h-5 w-5" />
          )}
          <span
            className={classNames('block truncate', {
              'text-gray-500 dark:text-gray-400': !selectedOption,
              'text-gray-900 dark:text-gray-100': selectedOption,
            })}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <IconSVG
          icon="chevron-down"
          className={classNames('h-5 w-5 text-gray-400', {
            'transform rotate-180': isOpen,
          })}
        />
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-500">{error}</p>
      )}
      
      {isOpen && (
        <div
          className={classNames(
            'absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            menuWidth || width
          )}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={classNames(
                'flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700',
                {
                  'bg-blue-50 dark:bg-blue-900/30': option.value === value,
                }
              )}
              onClick={() => handleOptionClick(option.value)}
            >
              {option.icon && (
                <IconSVG icon={option.icon} className="mr-2 h-5 w-5" />
              )}
              <span
                className={classNames('block truncate', {
                  'font-medium': option.value === value,
                  'font-normal': option.value !== value,
                })}
              >
                {option.label}
              </span>
              {option.value === value && (
                <IconSVG
                  icon="check"
                  className="ml-auto h-5 w-5 text-blue-600 dark:text-blue-400"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dropdown;